import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { WeddingTelegramCard } from "@wedding/wedding-telegram";
import { useWedding } from "../contexts/wedding-context";
import { useAdminApi } from "../hooks/use-admin-api";
import { createAdminBgApi } from "../lib/api";
import { QrCodeDisplay } from "../components/qr-code-display";
import {
  Loader2, QrCode, Upload, Trash2, ImageOff,
  CheckCircle2, XCircle,
} from "lucide-react";
import { BG_SLOTS, SUPPORTED_LANGUAGES, buildScanUrl } from "@wedding/shared";
import {
  SecondEventFields, secondEventFromWedding, secondEventPayload, secondEventKey,
  validateSecondEvent, type SecondEventValue,
  MainEventTypeField, mainEventPayload, validateMainEvent,
} from "@wedding/ui";
import type { Event2Label } from "@wedding/shared";
import { cn } from "../lib/utils";

function Toast({ msg, ok, onDone }: { msg: string; ok: boolean; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2800);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div className={cn(
      "fixed bottom-6 left-1/2 -translate-x-1/2 z-[200]",
      "flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium",
      ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white",
    )}>
      {ok ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
      {msg}
    </div>
  );
}

const WEB_BASE = (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20";

/**
 * ISO → `<input type="datetime-local">` değeri. `toISOString()` UTC verir;
 * datetime-local YEREL saat bekler → aradaki fark kadar kayardı (TR'de 3 saat).
 */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button" role="switch" aria-checked={checked} onClick={onChange}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        checked ? "bg-primary" : "bg-input",
      )}
    >
      <span className={cn(
        "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform",
        checked ? "translate-x-6" : "translate-x-1",
      )} />
    </button>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const api = useAdminApi();
  const { slug, wedding, refresh } = useWedding();
  const [activeTab, setActiveTab] = useState<"info" | "registration" | "backgrounds" | "notifications">("info");

  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = useCallback((msg: string, ok = true) => setToast({ msg, ok }), []);
  const dismissToast = useCallback(() => setToast(null), []);

  // ── Info ──────────────────────────────────────────────────
  const [infoForm, setInfoForm] = useState({
    title: wedding?.title ?? "",
    dateTime: wedding?.date_time?.slice(0, 16) ?? "",
    location: wedding?.location ?? "",
    description: wedding?.description ?? "",
  });
  // Opsiyonel ikinci etkinlik — `null` = tanımlı değil.
  const [secondEvent, setSecondEvent] = useState<SecondEventValue | null>(
    () => secondEventFromWedding(wedding),
  );
  // Ana etkinliğin türü — boş = eski davranış ("Düğün"). Organizer ile birebir.
  const [mainType, setMainType] = useState<{ label: Event2Label | ""; labelCustom: string }>(() => ({
    label: wedding?.event1_label ?? "",
    labelCustom: wedding?.event1_label_custom ?? "",
  }));
  const [infoFormSynced, setInfoFormSynced] = useState(!!wedding);
  const prevSlugRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (prevSlugRef.current !== undefined && prevSlugRef.current !== slug) setInfoFormSynced(false);
    prevSlugRef.current = slug;
  }, [slug]);

  useEffect(() => {
    if (!infoFormSynced && wedding) {
      setInfoForm({
        title: wedding.title ?? "",
        dateTime: wedding.date_time?.slice(0, 16) ?? "",
        location: wedding.location ?? "",
        description: wedding.description ?? "",
      });
      setSecondEvent(secondEventFromWedding(wedding));
      setMainType({
        label: wedding.event1_label ?? "",
        labelCustom: wedding.event1_label_custom ?? "",
      });
      setInfoFormSynced(true);
    }
  }, [wedding, infoFormSynced]);

  const [infoSaving, setInfoSaving] = useState(false);

  // Değişiklik yoksa Kaydet pasif kalır (organizer paneliyle birebir aynı).
  const infoDirty =
    infoForm.title !== (wedding?.title ?? "") ||
    infoForm.dateTime !== (wedding?.date_time?.slice(0, 16) ?? "") ||
    infoForm.location !== (wedding?.location ?? "") ||
    infoForm.description !== (wedding?.description ?? "") ||
    secondEventKey(secondEvent) !== secondEventKey(secondEventFromWedding(wedding)) ||
    mainType.label !== (wedding?.event1_label ?? "") ||
    mainType.labelCustom !== (wedding?.event1_label_custom ?? "");

  async function handleInfoSave(e: React.FormEvent) {
    e.preventDefault();
    const invalid = validateMainEvent(mainType.label, mainType.labelCustom)
      ?? validateSecondEvent(secondEvent);
    if (invalid) {
      showToast(t(invalid), false);
      return;
    }
    setInfoSaving(true);
    try {
      await api.patch("/info", {
        title: infoForm.title || undefined,
        dateTime: infoForm.dateTime || undefined,
        location: infoForm.location || undefined,
        description: infoForm.description || undefined,
        ...mainEventPayload(mainType.label, mainType.labelCustom),
        ...secondEventPayload(secondEvent),
      });
      showToast(t("wedding.updateSuccess"));
      refresh();
    } catch { showToast(t("wedding.updateFailed"), false); }
    finally { setInfoSaving(false); }
  }

  // ── Language ──────────────────────────────────────────────
  const [weddingLanguage, setWeddingLanguage] = useState(wedding?.config?.language ?? "tr");
  const [langSaving, setLangSaving] = useState(false);

  useEffect(() => {
    const lang = wedding?.config?.language;
    if (lang) setWeddingLanguage(lang);
  }, [wedding]);

  async function handleLanguageSave(lang: string) {
    setWeddingLanguage(lang);
    setLangSaving(true);
    try {
      await api.patch("/config/language", { language: lang });
      showToast(t("wedding.languageSaved"));
      refresh();
    } catch { showToast(t("wedding.languageSaveFailed"), false); }
    finally { setLangSaving(false); }
  }

  // ── RSVP ─────────────────────────────────────────────────
  const rsvpCfg: { showDietField?: boolean; showNotesField?: boolean; maxHeadcount?: number } = wedding?.config?.rsvp ?? {};
  const [showDietField, setShowDietField]   = useState(rsvpCfg.showDietField !== false);
  const [showNotesField, setShowNotesField] = useState(rsvpCfg.showNotesField !== false);
  const [maxHeadcount, setMaxHeadcount]     = useState(rsvpCfg.maxHeadcount ?? 10);
  const [rsvpSaving, setRsvpSaving]         = useState(false);

  useEffect(() => {
    const cfg: typeof rsvpCfg = wedding?.config?.rsvp ?? {};
    setShowDietField(cfg.showDietField !== false);
    setShowNotesField(cfg.showNotesField !== false);
    setMaxHeadcount(cfg.maxHeadcount ?? 10);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wedding?.config?.rsvp]);

  async function handleRsvpSave(e: React.FormEvent) {
    e.preventDefault();
    setRsvpSaving(true);
    try {
      await api.patch("/config/rsvp", { showDietField, showNotesField, maxHeadcount });
      showToast(t("registration.saved"));
      refresh();
    } catch { showToast(t("registration.saveFailed"), false); }
    finally { setRsvpSaving(false); }
  }

  // ── Registration ─────────────────────────────────────────
  const reg = wedding?.config?.registration ?? {};
  const [allowSelfRegister, setAllowSelfRegister] = useState(reg.allowSelfRegister !== false);
  const [requireOtp, setRequireOtp]               = useState(reg.requireOtp !== false);
  const [opensAtMode, setOpensAtMode]             = useState<"closed" | "scheduled">(
    reg.registrationOpensAt ? "scheduled" : "closed"
  );
  const [registrationOpensAt, setRegistrationOpensAt] = useState(
    reg.registrationOpensAt ? toLocalInput(reg.registrationOpensAt) : ""
  );
  const [registrationClosesAt, setRegistrationClosesAt] = useState(
    reg.registrationClosesAt ? toLocalInput(reg.registrationClosesAt) : ""
  );
  const [regSaving, setRegSaving] = useState(false);

  // Düğün context'i bu sayfadan SONRA yüklenebiliyor; sync olmazsa form
  // varsayılanları gösterir ve kaydet gerçek ayarların üzerine yazar.
  useEffect(() => {
    const cfg = wedding?.config?.registration ?? {};
    setAllowSelfRegister(cfg.allowSelfRegister !== false);
    setRequireOtp(cfg.requireOtp !== false);
    setOpensAtMode(cfg.registrationOpensAt ? "scheduled" : "closed");
    setRegistrationOpensAt(cfg.registrationOpensAt ? toLocalInput(cfg.registrationOpensAt) : "");
    setRegistrationClosesAt(cfg.registrationClosesAt ? toLocalInput(cfg.registrationClosesAt) : "");
  }, [wedding?.config?.registration]);

  // Değişiklik yoksa Kaydet pasif kalır.
  const registrationDirty =
    allowSelfRegister !== (reg.allowSelfRegister !== false) ||
    requireOtp !== (reg.requireOtp !== false) ||
    registrationOpensAt !== (reg.registrationOpensAt ? toLocalInput(reg.registrationOpensAt) : "") ||
    registrationClosesAt !== (reg.registrationClosesAt ? toLocalInput(reg.registrationClosesAt) : "");

  async function handleRegSave(e: React.FormEvent) {
    e.preventDefault();
    setRegSaving(true);
    if (!allowSelfRegister && opensAtMode === "scheduled" && !registrationOpensAt) {
      showToast(t("registration.opensAtRequired"), false);
      setRegSaving(false);
      return;
    }
    const opensAtIso = !allowSelfRegister && opensAtMode === "scheduled" && registrationOpensAt
      ? new Date(registrationOpensAt).toISOString()
      : null;
    const closesAtIso = registrationClosesAt ? new Date(registrationClosesAt).toISOString() : null;
    if (opensAtIso && closesAtIso && closesAtIso <= opensAtIso) {
      showToast(t("registration.closesAtBeforeOpens"), false);
      setRegSaving(false);
      return;
    }
    try {
      await api.patch("/config/registration", {
        allowSelfRegister,
        requireOtp: allowSelfRegister ? requireOtp : true,
        registrationOpensAt: opensAtIso,
        registrationClosesAt: closesAtIso,
      });
      showToast(t("registration.saved"));
      refresh();
    } catch { showToast(t("registration.saveFailed"), false); }
    finally { setRegSaving(false); }
  }

  // ── Backgrounds ───────────────────────────────────────────
  const bgSlots = [
    { slot: BG_SLOTS.GUEST_AUTH,      label: t("wedding.bgSlotAuth") },
    { slot: BG_SLOTS.GUEST_COUNTDOWN, label: t("wedding.bgSlotCountdown") },
    { slot: BG_SLOTS.GUEST_RSVP,      label: t("wedding.bgSlotRsvp") },
  ] as const;

  const [bgUrls, setBgUrls] = useState<Record<string, string | null>>(() => ({
    [BG_SLOTS.GUEST_AUTH]:      wedding?.bg_auth ?? null,
    [BG_SLOTS.GUEST_COUNTDOWN]: wedding?.bg_countdown ?? null,
    [BG_SLOTS.GUEST_RSVP]:      wedding?.bg_rsvp ?? null,
  }));
  const [bgHasOverride, setBgHasOverride] = useState<Record<string, boolean>>(() => ({
    [BG_SLOTS.GUEST_AUTH]:      !!wedding?.bg_auth_key,
    [BG_SLOTS.GUEST_COUNTDOWN]: !!wedding?.bg_countdown_key,
    [BG_SLOTS.GUEST_RSVP]:      !!wedding?.bg_rsvp_key,
  }));
  const [bgUploading, setBgUploading] = useState<Record<string, boolean>>({});
  const [bgDeleting,  setBgDeleting]  = useState<Record<string, boolean>>({});
  const bgFileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (wedding) {
      setBgUrls({
        [BG_SLOTS.GUEST_AUTH]:      wedding.bg_auth ?? null,
        [BG_SLOTS.GUEST_COUNTDOWN]: wedding.bg_countdown ?? null,
        [BG_SLOTS.GUEST_RSVP]:      wedding.bg_rsvp ?? null,
      });
      setBgHasOverride({
        [BG_SLOTS.GUEST_AUTH]:      !!wedding.bg_auth_key,
        [BG_SLOTS.GUEST_COUNTDOWN]: !!wedding.bg_countdown_key,
        [BG_SLOTS.GUEST_RSVP]:      !!wedding.bg_rsvp_key,
      });
    }
  }, [wedding]);

  async function handleBgUpload(slot: string, file: File) {
    setBgUploading((p) => ({ ...p, [slot]: true }));
    try {
      const res = await createAdminBgApi(slug).upload(slot, file);
      setBgUrls((p) => ({ ...p, [slot]: res.url }));
      setBgHasOverride((p) => ({ ...p, [slot]: true }));
      showToast(t("wedding.bgUploaded"));
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("wedding.bgUploadFailed"), false);
    } finally { setBgUploading((p) => ({ ...p, [slot]: false })); }
  }

  async function handleBgDelete(slot: string) {
    if (!confirm(t("common.confirm") + "?")) return;
    setBgDeleting((p) => ({ ...p, [slot]: true }));
    try {
      await createAdminBgApi(slug).delete(slot);
      setBgHasOverride((p) => ({ ...p, [slot]: false }));
      showToast(t("wedding.bgDeleted"));
      refresh();
    } catch { showToast(t("wedding.bgDeleteFailed"), false); }
    finally { setBgDeleting((p) => ({ ...p, [slot]: false })); }
  }

  const scanUrl = buildScanUrl(WEB_BASE, slug);

  const TAB_LABELS: Record<"info" | "registration" | "backgrounds" | "notifications", string> = {
    info:          t("wedding.infoTab"),
    registration:  t("wedding.registrationTab"),
    backgrounds:   t("wedding.bgTab"),
    notifications: t("wedding.notificationsTab"),
  };

  return (
    <div className="space-y-6">

      <div>
        <h1 className="font-serif italic text-3xl text-foreground">{t("registration.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("wedding.settingsHint")}</p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-border">
        <nav className="flex">
          {(["info", "registration", "backgrounds", "notifications"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={cn(
                "flex-1 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer text-center",
                activeTab === tab
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}>
              {TAB_LABELS[tab]}
            </button>
          ))}
        </nav>
      </div>

      {/* ── Info tab ── */}
      {activeTab === "info" && (
        <div className="space-y-4">
          <form onSubmit={handleInfoSave} className="bg-card border border-border rounded-2xl p-5 space-y-5">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                {t("wedding.slugReadOnly")}
              </p>
              <span className="inline-block font-mono text-sm bg-muted px-3 py-1.5 rounded-xl select-all">/{slug}</span>
            </div>
            <div className="border-t border-border" />
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
                {t("wedding.titlePlaceholder")}
              </label>
              <input type="text" value={infoForm.title} maxLength={200} placeholder={t("wedding.titlePlaceholder")}
                onChange={(e) => setInfoForm({ ...infoForm, title: e.target.value })} className={inputCls} />
              <p className="mt-1 text-xs text-muted-foreground text-right">{infoForm.title.length} / 200</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
                {t("wedding.datePlaceholder")}
              </label>
              <input type="datetime-local" value={infoForm.dateTime}
                onChange={(e) => setInfoForm({ ...infoForm, dateTime: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
                {t("wedding.locationPlaceholder")}
              </label>
              <input type="text" value={infoForm.location} maxLength={500} placeholder={t("wedding.locationPlaceholder")}
                onChange={(e) => setInfoForm({ ...infoForm, location: e.target.value })} className={inputCls} />
              <p className="mt-1 text-xs text-muted-foreground text-right">{infoForm.location.length} / 500</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 block">
                {t("wedding.descriptionPlaceholder")}
              </label>
              <textarea value={infoForm.description} rows={3} maxLength={2000}
                placeholder={t("wedding.descriptionPlaceholder")}
                onChange={(e) => setInfoForm({ ...infoForm, description: e.target.value })}
                className={cn(inputCls, "resize-none")} />
              <p className="mt-1 text-xs text-muted-foreground text-right">{infoForm.description.length} / 2000</p>
            </div>
            {/* Ana etkinliğin türü — boş bırakılırsa "Düğün" */}
            <MainEventTypeField
              label={mainType.label}
              labelCustom={mainType.labelCustom}
              onChange={setMainType}
              disabled={infoSaving}
            />

            {/* İkinci etkinlik (opsiyonel) — organizer paneliyle birebir aynı bileşen */}
            <SecondEventFields value={secondEvent} onChange={setSecondEvent} disabled={infoSaving} />
            <div className="flex justify-end pt-1">
              <button type="submit" disabled={infoSaving || !infoDirty}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer">
                {infoSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {infoSaving ? t("registration.saving") : t("registration.save")}
              </button>
            </div>
          </form>

          {/* Language */}
          <div className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <div>
              <h3 className="font-semibold text-sm">{t("wedding.language")}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{t("wedding.languageHint")}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <select value={weddingLanguage} onChange={(e) => setWeddingLanguage(e.target.value)}
                  disabled={langSaving}
                  className="w-full appearance-none rounded-xl border border-border bg-background px-4 py-2.5 pr-10 text-sm text-foreground disabled:opacity-50 cursor-pointer focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-colors">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>{lang.label}</option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6"/></svg>
                </div>
              </div>
              <button type="button" disabled={langSaving} onClick={() => handleLanguageSave(weddingLanguage)}
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer">
                {langSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {langSaving ? t("registration.saving") : t("registration.save")}
              </button>
            </div>
          </div>

          {/* RSVP details */}
          <form onSubmit={handleRsvpSave} className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <div>
              <h3 className="font-semibold text-sm">{t("registration.weddingDetails")}</h3>
              <p className="text-xs text-muted-foreground mt-0.5">{t("registration.weddingDetailsHint")}</p>
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{t("registration.showDietField")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("registration.showDietFieldHint")}</p>
              </div>
              <Toggle checked={showDietField} onChange={() => setShowDietField((v) => !v)} />
            </div>
            <div className="border-t border-border" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{t("registration.showNotesField")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("registration.showNotesFieldHint")}</p>
              </div>
              <Toggle checked={showNotesField} onChange={() => setShowNotesField((v) => !v)} />
            </div>
            <div className="border-t border-border" />
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block">
                {t("registration.maxHeadcount")}
              </label>
              <p className="text-xs text-muted-foreground">{t("registration.maxHeadcountHint")}</p>
              <input type="number" min={1} max={20} value={maxHeadcount}
                onChange={(e) => setMaxHeadcount(parseInt(e.target.value) || 1)}
                className="w-20 rounded-xl border border-border bg-background px-3 py-2 text-sm text-center focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20" />
            </div>
            <div className="flex justify-end pt-1">
              <button type="submit" disabled={rsvpSaving}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors cursor-pointer">
                {rsvpSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {rsvpSaving ? t("registration.saving") : t("registration.save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Registration tab ── */}
      {activeTab === "registration" && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-2xl p-6 flex flex-col items-center gap-4">
            <div className="flex items-center gap-2 self-start w-full">
              <QrCode className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm">{t("registration.qrCode")}</h3>
            </div>
            <p className="text-xs text-muted-foreground self-start">{t("registration.qrCodeHint")}</p>
            <QrCodeDisplay value={scanUrl} size={180} />
          </div>

          <form onSubmit={handleRegSave} className="bg-card border border-border rounded-2xl p-5 space-y-4">
            <h3 className="font-semibold text-sm">{t("wedding.registrationTab")}</h3>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{t("registration.allowSelfRegister")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("registration.allowSelfRegisterHint")}</p>
              </div>
              <Toggle checked={allowSelfRegister} onChange={() => setAllowSelfRegister((v) => !v)} />
            </div>
            {allowSelfRegister && (
              <>
                <div className="border-t border-border" />
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{t("registration.requireOtp")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t("registration.requireOtpHint")}</p>
                  </div>
                  <Toggle checked={requireOtp} onChange={() => setRequireOtp((v) => !v)} />
                </div>
              </>
            )}
            {!allowSelfRegister && (
              <div className="space-y-3 rounded-xl bg-muted/40 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="opensAtMode" value="closed"
                    checked={opensAtMode === "closed"} onChange={() => setOpensAtMode("closed")}
                    className="mt-0.5 cursor-pointer" />
                  <div>
                    <p className="text-sm font-medium">{t("registration.closedMode")}</p>
                    <p className="text-xs text-muted-foreground">{t("registration.closedModeHint")}</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="opensAtMode" value="scheduled"
                    checked={opensAtMode === "scheduled"} onChange={() => setOpensAtMode("scheduled")}
                    className="mt-0.5 cursor-pointer" />
                  <div>
                    <p className="text-sm font-medium">{t("registration.scheduledMode")}</p>
                    <p className="text-xs text-muted-foreground">{t("registration.opensAtHint")}</p>
                  </div>
                </label>
                {opensAtMode === "scheduled" && (
                  <div className="flex gap-2 pl-6">
                    <input type="datetime-local" aria-label={t("registration.opensAt")}
                      value={registrationOpensAt}
                      onChange={(e) => setRegistrationOpensAt(e.target.value)}
                      className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20" />
                    {wedding?.date_time && (
                      <button type="button"
                        onClick={() => setRegistrationOpensAt(toLocalInput(wedding.date_time!))}
                        className="rounded-xl border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap cursor-pointer">
                        {t("registration.useWeddingTime")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Kayıt penceresinin kapanışı — public QR linki düğünden sonra
                süresiz açık kalmasın. Boş = kapanış yok. */}
            <div className="border-t border-border" />
            <div className="space-y-2">
              <div>
                <p className="text-sm font-medium">{t("registration.closesAt")}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t("registration.closesAtHint")}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <input type="datetime-local" aria-label={t("registration.closesAt")}
                  value={registrationClosesAt}
                  onChange={(e) => setRegistrationClosesAt(e.target.value)}
                  className="min-w-[12rem] flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20" />
                {wedding?.date_time && (
                  <button type="button"
                    onClick={() => setRegistrationClosesAt(
                      toLocalInput(new Date(new Date(wedding.date_time!).getTime() + 7 * 86_400_000).toISOString())
                    )}
                    className="rounded-xl border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap cursor-pointer">
                    {t("registration.closesAtWeekAfterWedding")}
                  </button>
                )}
                {registrationClosesAt && (
                  <button type="button" onClick={() => setRegistrationClosesAt("")}
                    className="rounded-xl border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-accent transition-colors whitespace-nowrap cursor-pointer">
                    {t("registration.closesAtClear")}
                  </button>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <button type="submit" disabled={regSaving || !registrationDirty}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">
                {regSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                {regSaving ? t("registration.saving") : t("registration.save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Backgrounds tab ── */}
      {/* Telegram bildirimleri — organizatörle ortak kart (@wedding/wedding-telegram) */}
      {activeTab === "notifications" && <WeddingTelegramCard apiBase={`/api/admin/w/${slug}`} />}

      {activeTab === "backgrounds" && (
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {bgSlots.map(({ slot, label }) => {
              const url         = bgUrls[slot] ?? null;
              const hasOverride = bgHasOverride[slot] ?? false;
              const uploading   = bgUploading[slot] ?? false;
              const deleting    = bgDeleting[slot] ?? false;
              const busy        = uploading || deleting;
              return (
                <div key={slot} className="rounded-xl border border-border overflow-hidden">
                  <input
                    ref={(el) => { bgFileRefs.current[slot] = el; }}
                    type="file" accept="image/jpeg,image/png,image/webp,image/avif" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleBgUpload(slot, f); e.target.value = ""; }}
                  />
                  <div className="relative h-44 md:h-36 bg-muted group cursor-pointer"
                    onClick={() => !busy && bgFileRefs.current[slot]?.click()}>
                    {url ? (
                      <img src={url} alt={label} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
                        <ImageOff className="h-6 w-6" />
                        <span className="text-xs">{t("themes.upload")}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      {uploading ? <Loader2 className="h-5 w-5 text-white animate-spin" /> : <Upload className="h-5 w-5 text-white" />}
                    </div>
                  </div>
                  <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-foreground truncate">{label}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => bgFileRefs.current[slot]?.click()} disabled={busy}
                        title={url ? t("themes.change") : t("themes.upload")}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 cursor-pointer">
                        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                      </button>
                      {hasOverride && (
                        <button type="button" onClick={() => void handleBgDelete(slot)} disabled={busy}
                          title={t("themes.deleteSlot")}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 cursor-pointer">
                          {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={dismissToast} />}
    </div>
  );
}
